import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { useMe } from "../components/Shell.tsx";
import { Sticker } from "../components/Sticker.tsx";
import { ApiError, api, call } from "../lib/api.ts";
import { deliveryWindow, money } from "../lib/format.ts";
import type { Order } from "../lib/types.ts";

function countryName(code: string) {
  try {
    return new Intl.DisplayNames([navigator.language || "en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

const STAGES = [
  { key: "paid", label: "Paid", text: "We've got your order." },
  { key: "in_production", label: "Printing", text: "Your items are being made." },
  { key: "shipped", label: "Shipped", text: "On its way to you." },
  { key: "delivered", label: "Delivered", text: "Enjoy it!" },
] as const;
const RANK: Record<string, number> = {
  pending_payment: -1,
  paid: 0,
  in_production: 1,
  shipped: 2,
  delivered: 3,
};

export function StatusStrip({ status }: { status: Order["status"] }) {
  if (status === "canceled" || status === "refunded" || status === "failed") {
    const msg = {
      canceled: "This order was canceled.",
      refunded: "This order was refunded.",
      failed: "Payment didn't go through.",
    }[status];
    return (
      <div className="notice" data-tone="info">
        {msg}
      </div>
    );
  }
  const current = RANK[status] ?? -1;
  return (
    <ol className="statusstrip">
      {STAGES.map((s, i) => (
        <li key={s.key} data-state={i < current ? "done" : i === current ? "now" : "next"}>
          <span className="statusstrip__dot" aria-hidden="true">
            {i <= current ? <Icon name="check" size={18} /> : i + 1}
          </span>
          <strong>{s.label}</strong>
          <span>{s.text}</span>
          {i === current && <span className="visually-hidden">(current step)</span>}
        </li>
      ))}
    </ol>
  );
}

export function OrderPage() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") ?? undefined;
  const placed = params.get("placed") === "1";
  const me = useMe();
  const q = useQuery({
    queryKey: ["order", id, token],
    queryFn: () => call(api.orders[":id"].$get({ param: { id }, query: { t: token } })),
    refetchInterval: (query) => {
      const s = query.state.data?.order.status;
      return s && ["pending_payment", "paid", "in_production"].includes(s) ? 30_000 : false;
    },
  });

  if (q.error instanceof ApiError && q.error.status === 404) {
    return (
      <div className="sheet sheet--narrow">
        <h1 className="page-title">We can't show this order</h1>
        <p className="lede">
          Use the link from your confirmation email, or sign in with the email you ordered with.
        </p>
        <Link to="/account" className="btn" style={{ marginTop: "2rem" }}>
          Sign in
        </Link>
      </div>
    );
  }
  if (!q.data)
    return (
      <div className="sheet sheet--narrow">
        <div className="skeleton" style={{ height: 320 }} />
      </div>
    );
  const o = q.data.order;
  const a = o.shippingAddress;

  return (
    <div className="sheet sheet--narrow orderpage">
      {placed && (
        <Sticker face="var(--yolk)" tilt={-1.5} className="orderpage__thanks">
          <div className="orderpage__thanksbody">
            <h1>Thanks! It's going to print.</h1>
            <p>
              We emailed your receipt to <strong>{o.email}</strong>. This page updates as your order
              moves along.
            </p>
          </div>
        </Sticker>
      )}
      <header className="orderpage__head">
        {!placed && <h1 className="page-title">Order {o.number}</h1>}
        {placed && <h2 className="orderpage__number">Order {o.number}</h2>}
        <p className="muted">
          Placed {new Date(o.createdAt).toLocaleDateString(undefined, { dateStyle: "long" })}
        </p>
      </header>

      <StatusStrip status={o.status} />

      {o.shipments.length > 0 && (
        <div className="panel tracking">
          <Icon name="truck" size={24} />
          <div>
            <strong>Tracking</strong>
            {o.shipments.map((s) =>
              s.trackingUrl ? (
                <a
                  key={s.trackingCode}
                  href={s.trackingUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {s.carrier ? `${s.carrier}: ` : ""}
                  {s.trackingCode} <Icon name="external" size={14} />
                </a>
              ) : (
                <span key={s.trackingCode}>{s.trackingCode}</span>
              ),
            )}
          </div>
        </div>
      )}

      <section className="panel" aria-labelledby="ord-items">
        <h2 id="ord-items" className="co-h">
          Items
        </h2>
        <ul className="minilines">
          {o.items.map((i) => (
            <li key={i.id}>
              <span className="minilines__thumb">
                {i.previewUrl && <img src={i.previewUrl} alt="" />}
                <span className="minilines__qty tabular">{i.quantity}</span>
              </span>
              <span className="minilines__text">
                <strong>{i.title}</strong>
                <span>{i.variantTitle}</span>
              </span>
              <span className="tabular">{money(i.unitPriceCents * i.quantity, o.currency)}</span>
            </li>
          ))}
        </ul>
        <dl className="totals">
          <div>
            <dt>Subtotal</dt>
            <dd className="tabular">{money(o.subtotalCents, o.currency)}</dd>
          </div>
          <div>
            <dt>Shipping ({o.shippingMethod.name})</dt>
            <dd className="tabular">{money(o.shippingCents, o.currency)}</dd>
          </div>
          <div className="totals__grand">
            <dt>Total</dt>
            <dd className="tabular">{money(o.totalCents, o.currency)}</dd>
          </div>
        </dl>
      </section>

      <section className="panel orderpage__ship" aria-labelledby="ord-ship">
        <h2 id="ord-ship" className="co-h">
          Shipping to
        </h2>
        <address>
          {a.firstName} {a.lastName}
          <br />
          {a.addressLine1}
          {a.addressLine2 && (
            <>
              <br />
              {a.addressLine2}
            </>
          )}
          <br />
          {a.postCode} {a.city}
          {a.state ? `, ${a.state}` : ""}
          <br />
          {countryName(a.country)}
        </address>
        <p className="muted">
          {o.shippingMethod.name}:{" "}
          {deliveryWindow(o.shippingMethod.minDays, o.shippingMethod.maxDays)} after printing.
        </p>
      </section>

      {!me.data?.user && (
        <p className="muted orderpage__signin">
          Want all your orders and designs in one place?{" "}
          <Link to="/account">Sign in with {o.email}</Link>, no password needed.
        </p>
      )}
    </div>
  );
}
